export const TRACKING_FIELDS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'referrer'];
const TRACKING_STORAGE_KEY = 'tlf_tracking_data_v1';
export const GOOGLE_ADS_TAG_ID = 'AW-934083571';
export const GOOGLE_ADS_APPLY_SUBMISSION_LABEL = 'NHYPCN6JmIIcEPP3s70D';
export const FACEBOOK_PIXEL_ID = '570782379459578';
const FACEBOOK_CONVERSIONS_ENDPOINT = '/api/facebook_conversions';

function getEmptyTrackingData() {
  return {
    utm_source: null,
    utm_medium: null,
    utm_campaign: null,
    utm_term: null,
    utm_content: null,
    referrer: null,
  };
}

function normalizeTrackingValue(value) {
  if (value === null || value === undefined) {
    return null;
  }
  const normalized = String(value).trim();
  return normalized || null;
}

function getStorage() {
  if (typeof window === 'undefined') {
    return null;
  }
  return window.localStorage;
}

function readTrackingDataFromStorage() {
  const storage = getStorage();
  if (!storage) {
    return getEmptyTrackingData();
  }

  const rawJson = storage.getItem(TRACKING_STORAGE_KEY);
  if (!rawJson) {
    return getEmptyTrackingData();
  }

  try {
    const parsed = JSON.parse(rawJson);
    const result = getEmptyTrackingData();
    TRACKING_FIELDS.forEach((fieldName) => {
      result[fieldName] = normalizeTrackingValue(parsed?.[fieldName]);
    });
    return result;
  } catch (error) {
    return getEmptyTrackingData();
  }
}

function mergeTrackingData(baseData, incomingData) {
  const result = {...getEmptyTrackingData(), ...baseData};
  TRACKING_FIELDS.forEach((fieldName) => {
    const nextValue = normalizeTrackingValue(incomingData?.[fieldName]);
    if (nextValue !== null) {
      result[fieldName] = nextValue;
    }
  });
  return result;
}

function writeTrackingDataToStorage(trackingData) {
  const storage = getStorage();
  if (!storage) {
    return;
  }
  storage.setItem(TRACKING_STORAGE_KEY, JSON.stringify(trackingData));
}

function collectTrackingDataFromUrl() {
  if (typeof window === 'undefined') {
    return getEmptyTrackingData();
  }
  const urlParams = new URLSearchParams(window.location.search);
  const utmSource = normalizeTrackingValue(urlParams.get('utm_source'));
  const utmCampaign = normalizeTrackingValue(urlParams.get('utm_campaign'));
  const gadCampaignId = normalizeTrackingValue(urlParams.get('gad_campaignid'));
  const hasGoogleAdsCampaignId = gadCampaignId !== null;

  return {
    utm_source: utmSource ?? (hasGoogleAdsCampaignId ? 'google_ads' : null),
    utm_medium: normalizeTrackingValue(urlParams.get('utm_medium')),
    utm_campaign: utmCampaign ?? gadCampaignId,
    utm_term: normalizeTrackingValue(urlParams.get('utm_term')),
    utm_content: normalizeTrackingValue(urlParams.get('utm_content')),
    referrer: normalizeTrackingValue(document.referrer),
  };
}

export function getTrackingData() {
  return readTrackingDataFromStorage();
}

export function getTrackingDataWithEmptyStrings() {
  const rawData = readTrackingDataFromStorage();
  const normalizedData = {};
  TRACKING_FIELDS.forEach((fieldName) => {
    normalizedData[fieldName] = rawData[fieldName] || '';
  });
  return normalizedData;
}

export function updateTrackingData(incomingData) {
  const currentData = readTrackingDataFromStorage();
  const mergedData = mergeTrackingData(currentData, incomingData);
  writeTrackingDataToStorage(mergedData);
  return mergedData;
}

function ensureDataLayer() {
  if (typeof window === 'undefined') {
    return null;
  }
  if (!Array.isArray(window.dataLayer)) {
    window.dataLayer = [];
  }
  return window.dataLayer;
}

function callGtag(...args) {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    if (typeof window.gtag === 'function') {
      window.gtag(...args);
      return true;
    }

    const dataLayer = ensureDataLayer();
    if (dataLayer) {
      dataLayer.push(args);
      return true;
    }
  } catch (error) {
    if (process.env.NODE_ENV !== "test") {
      console.error('Google analytics call failed', error);
    }
  }

  return false;
}

export function trackAnalyticsEvent(eventName, payload = {}) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const dataLayer = ensureDataLayer();
    if (dataLayer) {
      dataLayer.push({
        event: eventName,
        ...payload,
      });
    }
    callGtag('event', eventName, payload);
  } catch (error) {
    if (process.env.NODE_ENV !== "test") {
      console.error(`Analytics event "${eventName}" failed`, error);
    }
  }
}

export function trackGoogleAdsConversion(label, payload = {}) {
  if (!label) {
    return;
  }

  trackAnalyticsEvent('google_ads_conversion', {
    conversion_label: label,
    google_ads_send_to: `${GOOGLE_ADS_TAG_ID}/${label}`,
    ...payload,
  });

  callGtag('event', 'conversion', {
    send_to: `${GOOGLE_ADS_TAG_ID}/${label}`,
    ...payload,
  });
}

function compactObject(payload) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return {};
  }

  const result = {};
  Object.entries(payload).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') {
      return;
    }
    result[key] = value;
  });
  return result;
}

export function buildMetaEventId(metaEventName) {
  const normalizedName = String(metaEventName || 'MetaEvent').replace(/[^A-Za-z0-9_.-]/g, '');
  return `${normalizedName}.${userBrowserId}.${Date.now()}`;
}

function getFacebookBrowserCookies() {
  return {
    fbp: normalizeTrackingValue(getCookie('_fbp')),
    fbc: normalizeTrackingValue(getCookie('_fbc')),
  };
}

function pushMetaPixelEvent(payload) {
  const dataLayer = ensureDataLayer();
  if (!dataLayer) {
    return;
  }

  dataLayer.push(compactObject({
    event: 'meta_conversion',
    meta_pixel_id: FACEBOOK_PIXEL_ID,
    meta_event_name: payload.eventName,
    event_id: payload.eventId,
    event_time: payload.eventTime,
    event_source_url: payload.eventSourceUrl,
    fbp: payload.fbp,
    fbc: payload.fbc,
    custom_data: compactObject(payload.customData),
  }));
}

async function sendFacebookConversion(payload) {
  try {
    await fetch(`${BACKEND_URL}${FACEBOOK_CONVERSIONS_ENDPOINT}`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify(payload),
    });
  } catch (error) {
    if (process.env.NODE_ENV !== "test") {
      console.error('Facebook conversion request failed', error);
    }
  }
}

export function trackMetaConversion({
  eventName,
  analyticsEventName,
  analyticsPayload = {},
  customData = {},
  userData = {},
}) {
  if (typeof window === 'undefined' || !eventName || !analyticsEventName) {
    return null;
  }

  const eventId = buildMetaEventId(eventName);
  const eventTime = Math.floor(Date.now() / 1000);
  const eventSourceUrl = window.location.href;
  const {fbp, fbc} = getFacebookBrowserCookies();
  const normalizedCustomData = compactObject(customData);
  const normalizedUserData = compactObject(userData);

  pushMetaPixelEvent({
    eventName,
    eventId,
    eventTime,
    eventSourceUrl,
    fbp,
    fbc,
    customData: normalizedCustomData,
  });

  void sendFacebookConversion({
    eventName,
    eventId,
    eventTime,
    eventSourceUrl,
    fbp,
    fbc,
    userBrowserId,
    customData: normalizedCustomData,
    userData: normalizedUserData,
  });

  trackAnalyticsEvent(analyticsEventName, {
    ...analyticsPayload,
    meta_event_name: eventName,
    meta_event_id: eventId,
  });

  return eventId;
}

export function trackTelegramConnectionSuccess(customData = {}) {
  try {
    window.ym(99696795, 'reachGoal', 'tg_connected');
  } catch (e) {
    console.error(e);
  }
  return trackMetaConversion({
    eventName: 'CompleteRegistration',
    analyticsEventName: 'telegram_connected',
    analyticsPayload: {
      application_step: 'telegram_auth',
      conversion_stage: 'partial',
      ...compactObject(customData),
    },
    customData: {
      application_step: 'telegram_auth',
      conversion_stage: 'partial',
      ...compactObject(customData),
    },
  });
}

export function trackApplicationSubmitSuccess({customData = {}, userData = {}} = {}) {
  const eventId = trackMetaConversion({
    eventName: 'Lead',
    analyticsEventName: 'application_submit_success',
    analyticsPayload: {
      application_step: 'submit',
      conversion_stage: 'full',
      ...compactObject(customData),
    },
    customData: {
      application_step: 'submit',
      conversion_stage: 'full',
      ...compactObject(customData),
    },
    userData,
  });
  trackGoogleAdsConversion(GOOGLE_ADS_APPLY_SUBMISSION_LABEL);
  return eventId;
}

const initialTrackingData = collectTrackingDataFromUrl();
updateTrackingData(initialTrackingData);

function resolveBackendUrl() {
  if (typeof window === 'undefined') {
    return '';
  }
  const {hostname, origin} = window.location;
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '0.0.0.0' || hostname === '::1';
  if (isLocalhost) {
    return 'http://localhost:3000';
  }
  return origin;
}

if (typeof window !== 'undefined' && !window.__BACKEND_URL__) {
  window.__BACKEND_URL__ = resolveBackendUrl();
}

if (typeof window !== 'undefined' && !window.__FACEBOOK_PIXEL_ID__) {
  window.__FACEBOOK_PIXEL_ID__ = FACEBOOK_PIXEL_ID;
}

function uuidv4() {
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
      (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16),
  );
}

function setCookie(name, value, days = 3650) {
  let expires = "";
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    expires = "; expires=" + date.toUTCString();
  }
  document.cookie = encodeURIComponent(name) + "=" + encodeURIComponent((value || "")) + expires + "; path=/";
}

function getCookie(name) {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) == ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
}

function eraseCookie(name) {
  document.cookie = encodeURIComponent(name) + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
}

const USER_ID_KEY = 'idd';

function getID() {
  // Сначала читаем его из куки
  const id1 = getCookie(USER_ID_KEY);
  const id2 = window.localStorage.getItem(USER_ID_KEY);
  let id = id1 || id2;
  if (!id) {
    id = uuidv4();
    setCookie(USER_ID_KEY, id);
    window.localStorage.setItem(USER_ID_KEY, id);
  } else {
    if (!id1) setCookie(USER_ID_KEY, id);
    if (!id2) window.localStorage.setItem(USER_ID_KEY, id);
  }
  return id;
}

export const userBrowserId = getID();

async function sendOpenData() {
  const trackingData = getTrackingDataWithEmptyStrings();
  const logData = {
    user_agent: navigator.userAgent,
    width: window.innerWidth,
    height: window.innerHeight,
    utm_source: trackingData.utm_source,
    url: window.location.href,
    idd: userBrowserId,
    referrer: trackingData.referrer,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
  try {
    const logRes = await fetch(`${BACKEND_URL}/api/opens`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify(logData),
    });
  } catch (e) {
    if (process.env.NODE_ENV !== "test") {
      console.error(e);
    }
  }
}

document.addEventListener('DOMContentLoaded', sendOpenData);


function insertLangUrls() {
  const currentUrl = window.location.href;
  const urlObj = new URL(currentUrl);

  const path = urlObj.pathname; // e.g. "/en/teachers.html"
  const newPathEn = path.replace(/^\/(en|ru)(\/|$)/, '/en$2');
  const newPathRu = path.replace(/^\/(en|ru)(\/|$)/, '/ru$2');

  const newUrlEn = urlObj.origin + newPathEn + urlObj.search + urlObj.hash;
  const newUrlRu = urlObj.origin + newPathRu + urlObj.search + urlObj.hash;

  document.getElementById('switch-en')?.setAttribute?.('href', newUrlEn);
  document.getElementById('switch-ru')?.setAttribute?.('href', newUrlRu);
}
document.addEventListener('DOMContentLoaded', insertLangUrls);


function isHeadlessMode() {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('headless') === 'true';
}

function updateLinkWithHeadlessParam(anchor) {
  const rawHref = anchor.getAttribute('href');
  if (!rawHref || rawHref.startsWith('#') || /^(mailto:|tel:|javascript:)/i.test(rawHref)) {
    return;
  }

  let resolvedUrl;
  try {
    resolvedUrl = new URL(rawHref, window.location.href);
  } catch (error) {
    return;
  }

  if (resolvedUrl.origin !== window.location.origin) {
    return;
  }
  if (resolvedUrl.protocol !== 'http:' && resolvedUrl.protocol !== 'https:') {
    return;
  }

  if (resolvedUrl.searchParams.get('headless') === 'true') {
    return;
  }

  resolvedUrl.searchParams.set('headless', 'true');
  anchor.setAttribute('href', resolvedUrl.toString());
}

function propagateHeadlessToInternalLinks() {
  if (!isHeadlessMode()) {
    return;
  }

  const updateNodeLinks = (node) => {
    if (!(node instanceof Element)) {
      return;
    }
    if (node.matches('a[href]')) {
      updateLinkWithHeadlessParam(node);
    }
    node.querySelectorAll('a[href]').forEach(updateLinkWithHeadlessParam);
  };

  updateNodeLinks(document.documentElement);

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes') {
        updateNodeLinks(mutation.target);
        return;
      }
      mutation.addedNodes.forEach(updateNodeLinks);
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['href'],
  });
}
document.addEventListener('DOMContentLoaded', propagateHeadlessToInternalLinks);


function removeHeaderInHeadlessMode() {
  if (isHeadlessMode()) {
    // Находим и удаляем все элементы header и footer
    document.querySelectorAll('header, footer').forEach(function(element) {
      element.remove();
    });
  }
}
document.addEventListener('DOMContentLoaded', removeHeaderInHeadlessMode);
